import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Banknote, TrendingUp, ArrowRight, Loader2, ArrowLeft, FileSignature, X, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Accept a raw token OR a pasted full/relative marketplace link and extract
// the token. This is the #1 cause of "token not accepted" — counterparties
// paste the whole share link into the token box.
const extractToken = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  const m = s.match(/[?&]market_token=([^&\s#]+)/);
  if (m) { try { return decodeURIComponent(m[1]); } catch { return m[1]; } }
  return s;
};


const PATH_LABELS = {
  spv_trust: "SPV Trust",
  discount_sale: "Outright Sale",
  financing: "Coll. Loan",
};

/**
 * Secondary Market — public browsable inventory of all open deals.
 * Buyers (via share-link) can see face value, purchase price, annualized
 * discount rate, and the spread over the live Treasury benchmark. One
 * click → Stripe Checkout for the discount price.
 */
export default function SecondaryMarket() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [buying, setBuying] = useState(null);
  const [tokenRequired, setTokenRequired] = useState(false);
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get("market_token");
  const [token, setToken] = useState(() => urlToken || sessionStorage.getItem("market_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [showRequest, setShowRequest] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reqForm, setReqForm] = useState({ company: "", contact_name: "", email: "" });
  const [offerModal, setOfferModal] = useState(null); // {item}
  const [offerForm, setOfferForm] = useState({
    buyer_name: "", buyer_address: "", buyer_signer_name: "", buyer_signer_title: "Authorized Officer",
    buyer_email: "", buyer_bank_name: "", buyer_routing: "", buyer_account: "", buyer_swift: "",
  });
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [offerSuccess, setOfferSuccess] = useState(null);
  const [paid, setPaid] = useState(null); // {session, tx, status, retrieval}

  // Handle the Stripe return: /market?paid=1&session=...&tx=...
  useEffect(() => {
    const sess = searchParams.get("session");
    if (!searchParams.get("paid") || !sess) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/checkout/status/${sess}`);
        if (stop) return;
        setPaid({ session: sess, tx: r.data.transaction_id, status: r.data.payment_status, retrieval: r.data.retrieval });
        if (r.data.payment_status !== "paid" && tries < 8) { tries += 1; setTimeout(poll, 1800); }
      } catch { if (tries < 8) { tries += 1; setTimeout(poll, 1800); } }
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    document.title = "Treasury House — Secondary Market";
    // Auto-persist the URL token so refreshes stay authenticated
    if (urlToken && urlToken !== sessionStorage.getItem("market_token")) {
      sessionStorage.setItem("market_token", urlToken);
      setToken(urlToken);
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/market/access-token-required`);
        setTokenRequired(r.data.required);
        if (!r.data.required || token || urlToken) refresh(urlToken || token);
      } catch {
        refresh();
      }
    })();
    // eslint-disable-next-line
  }, []);

  const refresh = async (overrideToken) => {
    setError(null);
    try {
      const t = overrideToken ?? token;
      const url = t ? `${API}/market/inventory?market_token=${encodeURIComponent(t)}` : `${API}/market/inventory`;
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const submitToken = () => {
    const t = extractToken(tokenInput);
    if (!t) { setError("Enter the access token or paste the full marketplace link."); return; }
    sessionStorage.setItem("market_token", t);
    setToken(t);
    refresh(t);
  };

  const requestToken = async () => {
    if (!reqForm.company.trim() || !reqForm.contact_name.trim() || !reqForm.email.trim()) {
      setError("Company, contact name and email are required to request access."); return;
    }
    setRequesting(true); setError(null);
    try {
      const r = await axios.post(`${API}/market/request-token`, reqForm);
      const t = r.data.token;
      sessionStorage.setItem("market_token", t);
      setToken(t);
      setShowRequest(false);
      refresh(t);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setRequesting(false);
  };

  const buy = async (tx_id) => {
    setBuying(tx_id);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/payments/checkout`, { transaction_id: tx_id, origin_url: origin });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setBuying(null);
    }
  };

  const openOffer = (item) => {
    setOfferModal({ item });
    setOfferSuccess(null);
    setOfferForm({
      buyer_name: "", buyer_address: "", buyer_signer_name: "", buyer_signer_title: "Authorized Officer",
      buyer_email: "", buyer_bank_name: "", buyer_routing: "", buyer_account: "", buyer_swift: "",
    });
  };

  const submitOffer = async () => {
    if (!offerForm.buyer_name.trim() || !offerForm.buyer_signer_name.trim()) {
      window.alert("Counterparty entity name and authorized signer name are required."); return;
    }
    setSubmittingOffer(true);
    try {
      const r = await axios.post(`${API}/market/offer`, {
        tx_id: offerModal.item.id,
        market_token: token,
        ...offerForm,
      });
      setOfferSuccess(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setSubmittingOffer(false);
  };

  if (tokenRequired && !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex items-center justify-center" data-testid="market-token-gate">
        <div className="bg-[#161616] border border-[#2A2A2A] rounded-sm p-8 max-w-md w-full">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] mb-2">Restricted Access</div>
          <h1 className="font-serif-display text-[26px] leading-tight mb-2">Treasury House Marketplace</h1>

          {!showRequest ? (
            <>
              <p className="text-[12px] text-[#A0A0A0] mb-6 leading-snug">This marketplace is for accredited institutional buyers. Enter the access token (or paste the full link) provided by the desk.</p>
              <input
                type="text"
                placeholder="Access token or marketplace link"
                value={tokenInput}
                onChange={e => setTokenInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && submitToken()}
                className="w-full bg-[#0F0F0F] border border-[#3A3A3A] text-[#F5F5F0] px-3 py-2 rounded-sm font-mono-fin text-[13px] mb-3"
                data-testid="market-token-input"
                autoFocus
              />
              <button
                onClick={submitToken}
                className="w-full bg-[#0B4636] hover:bg-[#0F5C48] text-white px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm"
                data-testid="market-token-submit"
              >
                Enter Marketplace
              </button>
              <div className="mt-4 pt-4 border-t border-[#2A2A2A] text-center">
                <span className="text-[11px] text-[#A0A0A0]">No token yet? </span>
                <button
                  onClick={() => { setShowRequest(true); setError(null); }}
                  className="text-[11px] text-[#B8860B] hover:text-[#d4a017] font-bold uppercase tracking-[0.12em]"
                  data-testid="market-request-token-open"
                >
                  Request Instant Access
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[12px] text-[#A0A0A0] mb-5 leading-snug">Request an instant access token. Provide your details — access is granted immediately and is subject to desk review.</p>
              <input type="text" placeholder="Company / Institution" value={reqForm.company}
                onChange={e => setReqForm(f => ({ ...f, company: e.target.value }))}
                className="w-full bg-[#0F0F0F] border border-[#3A3A3A] text-[#F5F5F0] px-3 py-2 rounded-sm text-[13px] mb-2.5" data-testid="market-request-company" />
              <input type="text" placeholder="Contact name" value={reqForm.contact_name}
                onChange={e => setReqForm(f => ({ ...f, contact_name: e.target.value }))}
                className="w-full bg-[#0F0F0F] border border-[#3A3A3A] text-[#F5F5F0] px-3 py-2 rounded-sm text-[13px] mb-2.5" data-testid="market-request-name" />
              <input type="email" placeholder="Work email" value={reqForm.email}
                onChange={e => setReqForm(f => ({ ...f, email: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && requestToken()}
                className="w-full bg-[#0F0F0F] border border-[#3A3A3A] text-[#F5F5F0] px-3 py-2 rounded-sm text-[13px] mb-3" data-testid="market-request-email" />
              <button
                onClick={requestToken}
                disabled={requesting}
                className="w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm flex items-center justify-center gap-2"
                data-testid="market-request-token-submit"
              >
                {requesting ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : "Generate & Enter"}
              </button>
              <button onClick={() => { setShowRequest(false); setError(null); }} className="w-full mt-2 text-[11px] text-[#A0A0A0] hover:text-[#F5F5F0]">← Back to token entry</button>
            </>
          )}
          {error && <div className="text-[11px] text-[#9E2A2B] mt-3 font-mono-fin">✗ {error}</div>}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return <div style={{ padding: 60, color: "#9E2A2B", fontFamily: "Manrope" }}>Market unavailable: {error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 60, color: "#A0A0A0", fontFamily: "Manrope" }}>Loading inventory…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]" data-testid="secondary-market">
      {/* Header */}
      <div className="border-b border-[#1F1F1F] bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div>
            <Link to="/" className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] hover:text-white inline-flex items-center gap-1 mb-2" data-testid="market-back">
              <ArrowLeft size={11} /> Back to Workbench
            </Link>
            <h1 className="font-serif-display text-[36px] leading-tight">Secondary Market</h1>
            <div className="text-[12px] text-[#A0A0A0] mt-1">Live inventory · Click any row to acquire the instrument at the standard <span className="text-[#7FB69A] font-bold">6.00% annualized discount</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0]">U.S. Treasury Benchmark</div>
            <div className="font-mono-fin text-[24px] text-[#7FB69A] font-bold mt-1" data-testid="market-benchmark">
              {data.benchmark_rate != null ? `${Number(data.benchmark_rate).toFixed(3)}%` : "—"}
            </div>
            <div className="text-[10px] text-[#5C5C5C] font-mono-fin">as of {data.benchmark_record_date || "—"}</div>
          </div>
        </div>
      </div>

      {/* Post-payment retrieval banner */}
      {paid && (
        <div className="max-w-7xl mx-auto px-8 pt-6" data-testid="market-paid-banner">
          <div className={`rounded-lg border p-5 ${paid.status === "paid" ? "border-[#0B6B4F]/60 bg-[#0B2A20]" : "border-[#B8860B]/50 bg-[#1A1606]"}`}>
            {paid.status === "paid" ? (
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <div className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#7FB69A]">Payment received — instrument acquired</div>
                  <div className="text-[12px] text-[#A0A0A0] mt-1">Retrieve your executed Private Negotiable Instrument instrument and the full funding binder below.</div>
                </div>
                <a href={`${API}/market/purchased/${paid.session}/binder`} target="_blank" rel="noreferrer"
                  data-testid="market-retrieve-binder"
                  className="ml-auto inline-flex items-center gap-2 bg-[#7FB69A] hover:bg-[#6aa588] text-[#0A0A0A] px-4 py-2.5 text-[12px] uppercase tracking-[0.12em] font-bold rounded-sm">
                  <ArrowRight size={14} /> Download Time Draft + Funding Binder
                </a>
              </div>
            ) : (
              <div className="text-[13px] text-[#E4C97A]">Confirming your payment… this can take a few seconds. This panel will update automatically.</div>
            )}
          </div>
        </div>
      )}

      {/* Summary stat strip */}
      <div className="max-w-7xl mx-auto px-8 py-6 grid grid-cols-3 gap-4 border-b border-[#1F1F1F]">
        <Stat label="Open Inventory" value={`${data.count} deals`} />
        <Stat label="Aggregate Face Value" value={`$${Number(data.total_face).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} accent />
        <Stat label="Aggregate Purchase Price" value={`$${Number(data.total_purchase).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
      </div>

      {/* Inventory table */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {data.inventory.length === 0 ? (
          <div className="text-center py-24 text-[#A0A0A0] font-serif-display text-[20px]">
            No inventory available. Originate new deals via the workbench.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="market-inventory-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-[#A0A0A0] border-b border-[#2A2A2A]">
                  <th className="py-3 pr-4">Instrument</th>
                  <th className="py-3 pr-4">Type</th>
                  <th className="py-3 pr-4 text-right">Face Value</th>
                  <th className="py-3 pr-4 text-right">Purchase</th>
                  <th className="py-3 pr-4 text-right">Discount %</th>
                  <th className="py-3 pr-4 text-right">Spread vs UST</th>
                  <th className="py-3 pr-4 text-right">Maturity</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {data.inventory.map(item => (
                  <tr key={item.id} className="border-b border-[#1F1F1F] hover:bg-[#161616] transition-colors" data-testid={`market-row-${item.id}`}>
                    <td className="py-4 pr-4 font-mono-fin text-[12px]">
                      <div className="font-bold text-[#F5F5F0]">{item.maker_name || item.trust_name}</div>
                      <div className="text-[10.5px] text-[#8A8A8A] mt-0.5 max-w-[320px] leading-snug" data-testid={`market-asset-desc-${item.id}`}>{item.asset_description}</div>
                    </td>
                    <td className="py-4 pr-4">
                      <span className="bg-[#1A2A22] text-[#7FB69A] px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.1em] font-bold">{PATH_LABELS[item.path] || item.path}</span>
                    </td>
                    <td className="py-4 pr-4 text-right font-mono-fin font-bold text-[14px]">${Number(item.face_value).toLocaleString()}</td>
                    <td className="py-4 pr-4 text-right font-mono-fin text-[#A0A0A0]">${Number(item.purchase_price).toLocaleString()}</td>
                    <td className="py-4 pr-4 text-right font-mono-fin">
                      <span className="text-[#7FB69A] font-bold">{item.discount_rate_annualized != null ? `${item.discount_rate_annualized}%` : "—"}</span>
                    </td>
                    <td className="py-4 pr-4 text-right font-mono-fin">
                      {item.treasury_spread_bps != null ? (
                        <span className={item.treasury_spread_bps > 0 ? "text-[#7FB69A]" : "text-[#9E2A2B]"}>
                          {item.treasury_spread_bps > 0 ? "+" : ""}{item.treasury_spread_bps} bps
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-4 pr-4 text-right text-[#A0A0A0] font-mono-fin text-[11px]">
                      {item.maturity_date} <span className="text-[#5C5C5C]">· {item.days_to_maturity}d</span>
                    </td>
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => openOffer(item)}
                          className="bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm inline-flex items-center gap-1"
                          data-testid={`market-offer-${item.id}`}
                          title="Submit a counterparty offer (click-to-buy)"
                        >
                          <FileSignature size={11} />
                          Make Offer
                        </button>
                        <button
                          onClick={() => buy(item.id)}
                          disabled={buying === item.id}
                          className="bg-[#0B4636] hover:bg-[#0F5C48] text-white disabled:opacity-50 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm inline-flex items-center gap-1"
                          data-testid={`market-buy-${item.id}`}
                          title="Pay via Stripe Checkout"
                        >
                          {buying === item.id ? <Loader2 size={11} className="animate-spin" /> : <Banknote size={11} />}
                          Pay Now
                          <ArrowRight size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div className="fixed bottom-6 right-6 bg-[#9E2A2B] text-white px-4 py-2 rounded-sm text-[12px] font-mono-fin">
          {error}
        </div>
      )}

      {/* ---------- Click-to-Buy: Counterparty Offer Modal ---------- */}
      {offerModal && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 overflow-y-auto py-8"
          data-testid="offer-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !submittingOffer) setOfferModal(null); }}
        >
          <div className="bg-[#13130E] border border-[#B8860B] rounded-md max-w-2xl w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#B8860B] font-bold mb-1">Counterparty Offer · Click to Buy</div>
                <h3 className="text-xl font-serif">{offerModal.item.trust_name}</h3>
                <div className="text-[12px] text-[#8A8A8A] mt-0.5 font-mono">
                  Face ${Number(offerModal.item.face_value).toLocaleString()} · Purchase ${Number(offerModal.item.purchase_price).toLocaleString()} · {offerModal.item.days_to_maturity}d
                </div>
              </div>
              <button onClick={() => !submittingOffer && setOfferModal(null)} className="text-[#5C5C5C] hover:text-[#ECECE4]"><X size={20} /></button>
            </div>

            {offerSuccess ? (
              <div className="text-center py-8" data-testid="offer-success">
                <CheckCircle2 className="text-[#7DD3B8] mx-auto mb-3" size={48} />
                <h4 className="text-lg font-serif text-[#ECECE4] mb-2">Offer Submitted</h4>
                <p className="text-[#8A8A8A] text-sm mb-1 max-w-md mx-auto">{offerSuccess.next_step}</p>
                <div className="mt-4 text-[10px] font-mono text-[#5C5C5C]">
                  Fingerprint: {offerSuccess.fingerprint.slice(0, 24)}…
                </div>
                <button
                  onClick={() => { setOfferModal(null); setOfferSuccess(null); }}
                  className="mt-6 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold rounded-sm"
                  data-testid="offer-close-btn"
                >Close</button>
              </div>
            ) : (
              <>
                <p className="text-[#8A8A8A] text-[12px] mb-4 leading-relaxed">
                  Your offer will replace the &quot;TO BE DESIGNATED&quot; placeholder on every binder document with your actual entity, signer, and bank instructions. Your click-to-sign is captured with a SHA-256 fingerprint for the closing record.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] block mb-1">Counterparty Entity <span className="text-[#9E2A2B]">*</span></label>
                    <input type="text" placeholder="Acme Capital Partners, LLC"
                      value={offerForm.buyer_name}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_name: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4]"
                      data-testid="offer-buyer-name" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] block mb-1">Address</label>
                    <input type="text" placeholder="100 Park Ave, New York, NY 10017"
                      value={offerForm.buyer_address}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_address: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4]"
                      data-testid="offer-buyer-address" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] block mb-1">Authorized Signer <span className="text-[#9E2A2B]">*</span></label>
                    <input type="text" placeholder="Jane K. Hartwell"
                      value={offerForm.buyer_signer_name}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_signer_name: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4]"
                      data-testid="offer-signer-name" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] block mb-1">Title</label>
                    <input type="text"
                      value={offerForm.buyer_signer_title}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_signer_title: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4]"
                      data-testid="offer-signer-title" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] block mb-1">Contact Email</label>
                    <input type="email" placeholder="jane.hartwell@acme.com"
                      value={offerForm.buyer_email}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_email: e.target.value }))}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4]"
                      data-testid="offer-email" />
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-[#2A2A22]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#B8860B] font-bold mb-2">Settlement Instructions (optional)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input type="text" placeholder="Beneficiary Bank Name"
                        value={offerForm.buyer_bank_name}
                        onChange={(e) => setOfferForm(f => ({ ...f, buyer_bank_name: e.target.value }))}
                        className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4] font-mono"
                        data-testid="offer-bank-name" />
                    </div>
                    <input type="text" placeholder="ABA Routing"
                      value={offerForm.buyer_routing}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_routing: e.target.value }))}
                      className="bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4] font-mono"
                      data-testid="offer-routing" />
                    <input type="text" placeholder="Account Number"
                      value={offerForm.buyer_account}
                      onChange={(e) => setOfferForm(f => ({ ...f, buyer_account: e.target.value }))}
                      className="bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm text-[#ECECE4] font-mono"
                      data-testid="offer-account" />
                  </div>
                </div>

                <div className="mt-5 p-3 rounded-sm bg-[#1a3a8e]/15 border border-[#1a3a8e]/40 text-[11px] text-[#A0B8FF]">
                  By clicking <strong>Submit Offer</strong>, you affirm you are authorized to bind <strong>{offerForm.buyer_name || "the named counterparty"}</strong>, and your electronic signature (UCC § 1-201(b)(37) · E-SIGN Act 15 U.S.C. § 7001) will be captured with timestamp + SHA-256 fingerprint.
                </div>

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    onClick={() => setOfferModal(null)}
                    disabled={submittingOffer}
                    className="text-[#8A8A8A] hover:text-[#ECECE4] text-[11px] uppercase tracking-[0.14em] px-4 py-2"
                  >Cancel</button>
                  <button
                    onClick={submitOffer}
                    disabled={submittingOffer || !offerForm.buyer_name || !offerForm.buyer_signer_name}
                    className="bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[11px] px-5 py-2 rounded-sm flex items-center gap-2"
                    data-testid="offer-submit-btn"
                  >
                    {submittingOffer ? <><Loader2 size={12} className="animate-spin" /> Submitting…</> : <><FileSignature size={12} /> Submit Offer · Click-to-Sign</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#161616] border border-[#2A2A2A] rounded-sm p-4">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#A0A0A0]">{label}</div>
      <div className={`font-mono-fin font-bold mt-1 ${accent ? "text-[#7FB69A] text-[26px]" : "text-[#F5F5F0] text-[22px]"}`}>{value}</div>
    </div>
  );
}
